// Copyright (C) 2020-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState, ReactPortal } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import { Row, Col } from 'antd/lib/grid';
import { Store } from 'antd/lib/form/interface';

import CVATTooltip from 'components/common/cvat-tooltip';

import { reviewActions, finishIssueAsync } from 'actions/review-actions';
import { useIsMounted } from 'utils/hooks';
import { Radio } from 'antd';
import { IssueType } from 'cvat-core/src/issue';

interface FormProps {
    top: number;
    left: number;
    angle: number;
    scale: number;
    fetching: boolean;
    submit(type: string, message: string): void;
    cancel(): void;
}

function MessageForm(props: FormProps): JSX.Element {
    const {
        top, left, angle, scale, fetching, submit, cancel,
    } = props;

    function handleSubmit(values: Store): void {
        submit(values.issue_type, values.issue_description);
    }

    return (
        <Form
            className='cvat-create-issue-dialog'
            style={{
                top, left, transform: `scale(${scale}) rotate(${angle}deg)`, minWidth: 260,
            }}
            onFinish={(values: Store) => handleSubmit(values)}
        >
            <Form.Item
                name='issue_description'
                rules={[{ required: true, message: '请填充字段' }]}
            >
                <Input autoFocus autoComplete='off' placeholder='请描述问题' />
            </Form.Item>
            <Form.Item
                name='issue_type'
                initialValue={IssueType.CERTAIN}
                rules={[{ required: true, message: '请选择类型' }]}
            >
                <Radio.Group className='cvat-radio-group-horizontal'>
                    <CVATTooltip
                        title='这是一个已经明确定义的确定问题，可以在规范中找到对应标注规则'
                        className='cvat-analytics-tooltip'
                        overlayStyle={{ maxWidth: '600px' }}
                    >
                        <Radio value={IssueType.CERTAIN}>明确</Radio>
                    </CVATTooltip>
                    <CVATTooltip
                        title='标注方严格按照旧的标注规范标注，但是质检方按照新的标注规范认为存在问题'
                        className='cvat-analytics-tooltip'
                        overlayStyle={{ maxWidth: '600px' }}
                    >
                        <Radio value={IssueType.OUTDATED}>过时</Radio>
                    </CVATTooltip>
                    <CVATTooltip
                        title='没有违反标注规范中明确定义的规则，但是质检人员直观上觉得存在问题'
                        className='cvat-analytics-tooltip'
                        overlayStyle={{ maxWidth: '600px' }}
                    >
                        <Radio value={IssueType.UNCERTAIN}>模糊</Radio>
                    </CVATTooltip>
                </Radio.Group>
            </Form.Item>
            <Row justify='space-between'>
                <Col>
                    <Button
                        onClick={cancel}
                        disabled={fetching}
                        type='ghost'
                        className='cvat-create-issue-dialog-cancel-button'
                    >
                        取消
                    </Button>
                </Col>
                <Col>
                    <Button
                        loading={fetching}
                        disabled={fetching}
                        type='primary'
                        htmlType='submit'
                        className='cvat-create-issue-dialog-submit-button'
                    >
                        提交
                    </Button>
                </Col>
            </Row>
        </Form>
    );
}

interface Props {
    top: number;
    left: number;
    angle: number;
    scale: number;
}

export default function CreateIssueDialog(props: Props): ReactPortal {
    const [fetching, setFetching] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {
        top, left, angle, scale,
    } = props;

    return ReactDOM.createPortal(
        <MessageForm
            top={top}
            left={left}
            angle={angle}
            scale={scale}
            fetching={fetching}
            submit={(type: string, message: string) => {
                setFetching(true);
                dispatch(finishIssueAsync(type, message)).finally(() => {
                    if (isMounted()) {
                        setFetching(false);
                    }
                });
            }}
            cancel={() => {
                dispatch(reviewActions.cancelIssue());
            }}
        />,
        window.document.getElementById('cvat_canvas_attachment_board') as HTMLElement,
    );
}
